import {ReactNode} from 'react';

import {CheckStatus, DocumentStatus, DocumentType, ProductsStatus} from 'core/dictionaries';

import {PackageTypes, PackagingType} from './gtins';

export enum DocumentsAccessors {
    id = 'reportId',
    irn = 'irn',
    createdDate = 'createdAt',
    owner = 'owner',
    seller = 'seller',
    buyer = 'buyer',
    type = 'type',
    status = 'status',
    side = 'side',
    sellerLocation = 'sellerLocation',
    buyerLocation = 'buyerLocation',
}

export interface DocumentsGridRawRow {
    irn: string;
    reportId: string;
    createdAt: string;
    type: string;
    owner: string;
    seller: string | null;
    buyer: string | null;
    status: DocumentStatus;
    sellerLocation: string;
    buyerLocation: string;
}

export interface DocumentsGridFormattedRow {
    [DocumentsAccessors.id]: string;
    [DocumentsAccessors.createdDate]: string;
    [DocumentsAccessors.owner]?: string;
    [DocumentsAccessors.seller]?: string;
    [DocumentsAccessors.buyer]?: string;
    [DocumentsAccessors.type]?: number;
    [DocumentsAccessors.status]: string;
    [DocumentsAccessors.sellerLocation]?: string;
    [DocumentsAccessors.buyerLocation]?: string;
}

export enum MovingAccessors {
    id = 'reportId',
    createdDate = 'createdAt',
    status = 'status',
    employeeFullName = 'employeeFullName',
    storageLocationFrom = 'storageLocationFrom',
    storageLocationTo = 'storageLocationTo',
    tin = 'tin',
}

export interface MovingGridRawRow {
    reportId: string;
    createdAt: string;
    status: DocumentStatus;
    employeeFullName: string;
    storageLocationFrom: string;
    storageLocationTo: string;
    tin?: string;
}

export interface MovingGridFormattedRow {
    [MovingAccessors.id]: string;
    [MovingAccessors.createdDate]: string;
    [MovingAccessors.status]: ReactNode | string | null;
    [MovingAccessors.storageLocationFrom]?: string | null;
    [MovingAccessors.storageLocationTo]?: string | null;
    [MovingAccessors.tin]?: string;
}

export enum GdsAccessors {
    id = 'id',
    customsGdNumber = 'customsGdNumber',
    customsCode = 'customsCode',
    dateOfReceipt = 'receiptedAt',
    status = 'status',
}

export interface GdsGridRawRow {
    id: string;
    customsGdNumber: string;
    customsCode: string;
    receiptedAt: string;
    participantTin: string;
    participantName: string;
    status: DocumentStatus;
}

export interface GdsGridFormattedRow {
    [GdsAccessors.id]: string;
    [GdsAccessors.customsGdNumber]: string;
    [GdsAccessors.customsCode]: string;
    [GdsAccessors.dateOfReceipt]: string;
    [GdsAccessors.status]: string;
}
export enum ChecksAccessors {
    id = 'receiptId',
    createdDate = 'createdAt',
    checkType = 'operationType',
    seller = 'manufacturerName',
    status = 'status',
}

export interface ChecksGridRawRow {
    receiptId: string;
    createdAt: string;
    operationType: string;
    manufacturerName: string;
    status: string;
}

export interface ReceiptDetailRaw {
    createdAt: string;
    receiptId: string;
    user: string;
    retailAddress: string;
    userInn: string;
    taxationType: number;
    operator: string;
    shiftNumber: number;
    dateTime: string;
    fiscalDocumentNumber: number;
    fiscalDriveNumber: string;
    productsTotalCount: number;
    status: CheckStatus;
    errors?: ReceiptDetailError[];
}

export interface ReceiptDetail {
    createdAt: string;
    receiptId: string;
    user: string;
    retailAddress: string;
    userInn: string;
    taxationType: number;
    operator: string;
    shiftNumber: number;
    dateTime: string;
    fiscalDocumentNumber: number;
    fiscalDriveNumber: string;
    productsTotalCount: number;
    status: string;
    errors?: string[];
}

export interface ReceiptDetailError {
    code: string;
    message: string;
}

export interface ChecksGridFormattedRow {
    [ChecksAccessors.id]: ReactNode;
    [ChecksAccessors.createdDate]: string;
    [ChecksAccessors.checkType]: string;
    [ChecksAccessors.seller]: string;
    [ChecksAccessors.status]: string;
}

export enum DocumentsDropOutProductsAssessors {
    code = 'code',
    gtin = 'gtin',
    productName = 'productName',
    productGroupCode = 'productGroupCode',
}
export interface DropOutFormData {
    reason: string;
    salesChannel: string;
    documentId?: string;
    supportingDocumentNumber?: string;
    supportingDocumentDate?: Date | null;
    comment?: string;
    attachmentFiles?: File[];
    counterpartyId?: string;
    counterpartyName?: string;
    destinationCountry?: string;
    resultMarkingCodes?: string[];
}

export interface DocumentDropOutData extends Omit<DropOutFormData, 'documentId'> {
    codes: Array<string>;
    documentId: string;
}

export interface DropoutDictionaryResponse {
    reasons: string[];
    salesChannels: string[];
}
export interface AggregationFormData {
    aggregationType: string;
    aggregationNumber: string | null;
    storageLocationId: number | null;
    storageLocationName?: string;
}

export interface DocumentAggregationData extends AggregationFormData {
    codes?: Array<string>;
    file?: File | null;
}
export interface UtilisationFormData {
    batchNumberSource?: 'auto' | 'manual';
    groupMarkingCode?: string | null;
    factoryId: string;
    factoryName?: string;
    productionLineId?: string;
    productionLineName?: string;
    applicationDate: Date | string | null;
    manufacturingDate: Date | string | null;
    storageLocationId: string | null;
    storageLocationName?: string;
    batchNumber?: string;
    batchNumberAutoGeneratedHolder?: string | null;
}

export interface DocumentUtilisationData extends UtilisationFormData {
    codes: Array<string>;
    file?: File | null;
}
export interface CommissioningFormData {
    startCirculationDate: Date | null;
}

export interface DocumentCommissioningData extends CommissioningFormData {
    codes: Array<string>;
}

export interface MovingFormData {
    employeeFullName?: string;
    storageLocationFrom: string;
    storageLocationTo: string;
    storageLocationFromId?: string | null;
    storageLocationToId?: string | null;
    note?: string;
}

export interface DocumentMovingData extends MovingFormData {
    codes: Array<string>;
    file?: File | null;
}

/*
  Document details
*/
export interface DocumentDetailCommon {
    reportId: string;
    status: DocumentStatus;
    createdAt: string;
    productsTotalCount?: number;
    errors?:
        | {
              code: string;
              message: string;
          }[]
        | null;
}
export interface DropOutReport extends DocumentDetailCommon {
    reportType: 'DROPOUT';
    reportInfo: {
        reason: string;
        createdAt: string;
        attachment?: string[];
    };
}
export interface IntroduceReport extends DocumentDetailCommon {
    reportType: 'INTRODUCE';
    reportInfo: {
        producedAt: string;
        manufacturerName: string;
    };
}
export interface UPDReport extends DocumentDetailCommon {
    reportType: 'UPD';
    reportInfo: {
        processedAt: string;
        buyerLegalName: string;
        sellerLegalName: string;
        currency: string;
    };
}
export interface UtilizationReport extends DocumentDetailCommon {
    reportType: 'UTILISATION';
    reportInfo: {
        createdAt: string;
        factoryName: string;
        productionLineName: string;
        storageLocation?: AggregationStorageLocation;
        batchNumber?: string;
        manufacturingDate: string;
    };
}
export interface MovingReport extends DocumentDetailCommon {
    reportType: 'MOVING';
    reportInfo: {
        employeeFullName: string;
        note?: string;
        storageLocationFrom?: string;
        storageLocationTo?: string;
    };
}
export interface AggregationNumbers {
    aggregationNumber: string;
    packagingType: string;
}

type Nullable<T> = {
    [P in keyof T]: T[P] | null;
};
export interface StorageLocationAddress {
    address: string;
    city: string;
    country: string;
    gln: string;
    gps: string;
    id: number;
}

export type NullableStorageLocationAddress = Nullable<StorageLocationAddress>;
export interface AggregationStorageLocation {
    address: NullableStorageLocationAddress;
    clientId: number;
    storageName: string;
}
export interface AggregateReport extends DocumentDetailCommon {
    reportType: 'AGGREGATE';
    reportInfo: {
        createdAt: string;
        groupPackageCount: number | string;
        transportPackageCount?: number | string;
        aggregationNumbers?: Array<AggregationNumbers>;
        storageLocation?: AggregationStorageLocation;
    };
}

export type DocumentDetail =
    | DropOutReport
    | IntroduceReport
    | UtilizationReport
    | AggregateReport
    | UPDReport
    | MovingReport;

export interface GtinDetailRaw {
    templateId: string;
    gtin: string;
    productId: string;
    productName: string;
    productVendorCode: string;
    quantity: number;
}

export enum ProductsUPDAccessors {
    gtin = 'gtin',
    quantity = 'quantity',
    productGroupCode = 'productGroupCode',
    productName = 'productName',
}

export interface ProductsUPDItem {
    gtin: string;
    quantity: string;
    productGroupCode: string;
    productName: string;
}

export interface AggregationParentCode {
    markingCode: string;
    packagingType: PackagingType;
    status: DocumentType;
    storageLocationId: number | null;
    hasChildren: boolean;
}

export interface AggregationChildrenCode {
    markingCode: string;
    packagingType: PackagingType;
    status: DocumentType;
    storageLocationId: number | null;
    productName: string | null;
    parentMarkingCode: string | null;
}

export type DocumentCodesCache = Record<string, DocumentsCodesTreeRow[]>;

export type DocumentsCodesTreeRow = {
    identityCode: string;
    gtin: string;
    status: ProductsStatus;
    productName: string;
    packagingType: PackageTypes;
};
